"use client";

import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  FaArrowRight,
  FaCheck,
  FaFilePdf,
} from "react-icons/fa6";
import { HiSparkles } from "react-icons/hi2";
import WebsiteAuditRequestPopup, {
  getWebsiteUrlError,
  isValidWebsiteUrl,
} from "@/components/common/WebsiteAuditRequestPopup";
import GradientButton from "@/components/ui/GradientButton";
import Input from "@/components/ui/Input";

const ease = [0.17, 0.85, 0.438, 0.99] as const;

const trackedEngines = [
  { name: "ChatGPT", bg: "bg-emerald-600", delay: "0.1s" },
  { name: "Google AI Overviews", bg: "bg-blue-600", delay: "0.2s" },
  { name: "Gemini", bg: "bg-violet-600", delay: "0.3s" },
  { name: "Perplexity", bg: "bg-cyan-600", delay: "0.4s" },
  { name: "Microsoft Copilot", bg: "bg-pink-600", delay: "0.5s" },
];

export default function AiSearchHero() {
  const reduceMotion = useReducedMotion();
  const [chatTime, setChatTime] = useState(0);
  const [score, setScore] = useState(0);
  const [website, setWebsite] = useState("");
  const [websiteError, setWebsiteError] = useState("");
  const [isAuditPopupOpen, setIsAuditPopupOpen] = useState(false);

  useEffect(() => {
    if (reduceMotion) return;

    const start = performance.now();
    const duration = 1500;
    let scoreFrame: number;
    const animateScore = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setScore(Math.round(eased * 78));
      if (p < 1) scoreFrame = requestAnimationFrame(animateScore);
    };
    scoreFrame = requestAnimationFrame(animateScore);

    const chatInterval = setInterval(() => {
      setChatTime((prev) => (prev + 70 >= 9800 ? 0 : prev + 70));
    }, 70);

    return () => {
      cancelAnimationFrame(scoreFrame);
      clearInterval(chatInterval);
    };
  }, [reduceMotion]);

  const question = "Who builds web and mobile apps for startups in India?";
  const displayedScore = reduceMotion ? 78 : score;
  const simulationTime = reduceMotion ? 6000 : chatTime;
  const typedLength = reduceMotion
    ? question.length
    : Math.min(question.length, Math.floor(simulationTime / 34));
  const ansStart = 2700;
  const showAnswer = reduceMotion || simulationTime > ansStart;
  const isThinking =
    !reduceMotion && simulationTime > 1900 && simulationTime <= ansStart;
  const isCited = reduceMotion || simulationTime > 4300;

  const reveal = {
    initial: reduceMotion ? false : { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: reduceMotion ? 0 : 0.65, ease },
  };

  return (
    <section className="relative overflow-hidden bg-linear-to-br from-white via-[#FFF6F9] to-[#F4F0FF] py-14 sm:py-20 lg:py-24 transition-colors duration-300 dark:from-gray-950 dark:via-gray-950 dark:to-slate-900">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-14 xl:gap-16">
          <motion.div
            {...reveal}
            className="flex flex-col gap-6 lg:col-span-6 xl:col-span-7"
          >
            <div className="inline-flex items-center gap-2">
              <span className="font-poppins text-sm font-bold uppercase tracking-widest text-pink-700 dark:text-pink-400">
                AI Search Optimization · GEO & AEO
              </span>
            </div>

            <h1 className="font-poppins text-4xl font-bold leading-tight tracking-tight text-indigo-950 dark:text-white sm:text-5xl lg:text-5xl">
              Get Your Brand Found in AI{" "}
              <span className="text-pink-600 dark:text-pink-400">Answers</span>
            </h1>

            <p className="max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg dark:text-slate-300">
              Customers now ask ChatGPT, Gemini, Perplexity and Google&apos;s AI
              Overviews for recommendations. We help your business show up — and
              get cited — when they do, starting with a free AI visibility audit
              and a clear action plan.
            </p>

            <ul className="flex flex-col gap-3 font-medium text-indigo-950 dark:text-slate-200">
              <li className="flex items-center gap-3 text-sm sm:text-base">
                <span className="grid size-6 place-items-center rounded-full bg-pink-600 text-white shadow-sm shadow-pink-600/30">
                  <FaCheck aria-hidden className="text-xs" />
                </span>
                Free AI visibility audit
              </li>
              <li className="flex items-center gap-3 text-sm sm:text-base">
                <span className="grid size-6 place-items-center rounded-full bg-pink-600 text-white shadow-sm shadow-pink-600/30">
                  <FaCheck aria-hidden className="text-xs" />
                </span>
                See where competitors get cited instead of you
              </li>
              <li className="flex items-center gap-3 text-sm sm:text-base">
                <span className="grid size-6 place-items-center rounded-full bg-pink-600 text-white shadow-sm shadow-pink-600/30">
                  <FaCheck aria-hidden className="text-xs" />
                </span>
                Expert GEO &amp; AEO action plan
              </li>
            </ul>

            <div className="mt-2 max-w-xl space-y-2">
              <form
                noValidate
                onSubmit={(event) => {
                  event.preventDefault();
                  const error = getWebsiteUrlError(website);
                  setWebsiteError(error);
                  if (!error) setIsAuditPopupOpen(true);
                }}
                className="flex flex-col gap-3 sm:flex-row sm:items-start"
              >
                <div className="min-w-0 flex-1">
                  <Input
                    id="ai-search-url"
                    name="website"
                    type="url"
                    label="Your website URL"
                    labelClassName="sr-only !mb-0"
                    placeholder="https://yourwebsite.com"
                    value={website}
                    error={websiteError}
                    reserveErrorSpace={false}
                    onChange={(event) => {
                      const value = event.target.value;
                      setWebsite(value);
                      if (isValidWebsiteUrl(value)) setWebsiteError("");
                    }}
                    className="!h-13 !rounded-xl !border !border-slate-200 !font-poppins !text-indigo-950 shadow-xs focus:!border-pink-500 focus:!ring-2 focus:!ring-pink-500/20 dark:!border-slate-700 dark:!bg-slate-900 dark:!text-white"
                  />
                </div>
                <GradientButton
                  type="submit"
                  variant="sunset-pink"
                  className="h-13 shrink-0 px-6 font-semibold whitespace-nowrap"
                >
                  Check My AI Visibility <FaArrowRight aria-hidden />
                </GradientButton>
              </form>
            </div>
            {/* <p className="text-sm text-slate-500 dark:text-slate-400">
              No credit card required. We&apos;ll email your report with clear
              next steps.
            </p> */}
          </motion.div>

          {/* Right Column: Interactive Animated AI Visual */}
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: reduceMotion ? 0 : 0.8, delay: 0.1, ease }}
            className="relative mx-auto w-full max-w-xl lg:col-span-6 xl:col-span-5 pb-16 lg:pb-0"
          >
            {/* Main Chat Box */}
            <div className="relative rounded-2xl border border-slate-200/90 bg-white shadow-2xl shadow-indigo-950/15 overflow-hidden dark:border-slate-800 dark:bg-slate-900">
              {/* Header */}
              <div className="flex items-center gap-2.5 border-b border-slate-100 px-4 py-3 dark:border-slate-800">
                <span className="grid size-7 place-items-center rounded-lg bg-linear-to-br from-violet-600 to-blue-600 text-white">
                  <HiSparkles aria-hidden className="text-sm" />
                </span>
                <span className="font-poppins text-xs font-bold text-indigo-950 dark:text-white">
                  AI Assistant
                </span>
                <span className="ml-auto text-[11px] font-medium text-slate-400">
                  New chat
                </span>
              </div>

              {/* Chat Body */}
              <div className="flex min-h-64 flex-col gap-3 bg-slate-50/70 p-4 dark:bg-slate-950/60">
                {/* User Prompt */}
                <div className="self-end max-w-[85%] rounded-2xl rounded-tr-xs bg-indigo-950 px-3.5 py-2.5 text-xs leading-relaxed text-white shadow-sm dark:bg-pink-700">
                  <span>{question.slice(0, typedLength)}</span>
                  {typedLength < question.length && (
                    <span className="ml-0.5 inline-block h-3.5 w-0.5 animate-pulse bg-white align-middle" />
                  )}
                </div>

                {/* Thinking Dots */}
                {isThinking && (
                  <div className="self-start inline-flex items-center gap-1 rounded-2xl rounded-tl-xs border border-slate-200 bg-white px-3 py-2 text-xs shadow-xs dark:border-slate-800 dark:bg-slate-900">
                    <span className="size-1.5 animate-bounce rounded-full bg-slate-400" />
                    <span className="size-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:0.15s]" />
                    <span className="size-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:0.3s]" />
                  </div>
                )}

                {/* AI Response Bubble */}
                {showAnswer && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35 }}
                    className="self-start max-w-[92%] rounded-2xl rounded-tl-xs border border-slate-200 bg-white p-3.5 shadow-sm dark:border-slate-800 dark:bg-slate-900 flex flex-col gap-2 text-xs text-slate-700 dark:text-slate-200"
                  >
                    <p className="text-slate-600 dark:text-slate-300">
                      Here are some well-reviewed options:
                    </p>
                    <p className="font-semibold text-indigo-950 dark:text-white">
                      <strong className="text-pink-600 dark:text-pink-400 font-bold">
                        The Dezine
                      </strong>{" "}
                      — web, mobile &amp; AI development since 2014{" "}
                      <span className="inline-flex size-4 items-center justify-center rounded bg-pink-100 text-[10px] font-extrabold text-pink-700 dark:bg-pink-950 dark:text-pink-300">
                        1
                      </span>
                    </p>
                    <div className="h-1.5 w-11/12 rounded-full bg-slate-100 dark:bg-slate-800" />
                    <div className="h-1.5 w-3/4 rounded-full bg-slate-100 dark:bg-slate-800" />

                    {/* Citations row */}
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold transition-all ${
                          isCited
                            ? "bg-pink-50 text-pink-700 ring-2 ring-pink-500/30 dark:bg-pink-950/60 dark:text-pink-300"
                            : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                        }`}
                      >
                        <span className="grid size-3.5 place-items-center rounded-full bg-pink-600 text-[8px] text-white">
                          1
                        </span>
                        thedezine.in
                      </span>
                      <span className="h-5 w-16 rounded-full bg-slate-100 dark:bg-slate-800" />
                      <span className="h-5 w-12 rounded-full bg-slate-100 dark:bg-slate-800" />
                    </div>
                  </motion.div>
                )}
              </div>
            </div>

            {/* Floating Card 1: Visibility Score (Top-Right) */}
            <motion.div
              animate={reduceMotion ? {} : { y: [0, -8, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -right-3 -top-6 z-20 flex w-36 flex-col items-center gap-1 rounded-2xl border border-slate-200/90 bg-white p-3 shadow-xl backdrop-blur-sm dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="relative h-14 w-24">
                <svg
                  viewBox="0 0 100 60"
                  className="h-full w-full overflow-visible"
                >
                  <defs>
                    <linearGradient
                      id="aiHeroGradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="0%"
                    >
                      <stop offset="0%" stopColor="#F7845A" />
                      <stop offset="55%" stopColor="#E11D63" />
                      <stop offset="100%" stopColor="#7B3FE4" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M10 50a40 40 0 0 1 80 0"
                    fill="none"
                    stroke="#F1F2F7"
                    strokeWidth="8"
                    strokeLinecap="round"
                    className="dark:stroke-slate-800"
                  />
                  <motion.path
                    d="M10 50a40 40 0 0 1 80 0"
                    fill="none"
                    stroke="url(#aiHeroGradient)"
                    strokeWidth="8"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 0.78 }}
                    transition={{ duration: reduceMotion ? 0 : 1.5, ease }}
                  />
                </svg>
                <span className="absolute inset-x-0 bottom-0 text-center font-poppins text-2xl font-extrabold text-indigo-950 dark:text-white">
                  {displayedScore}
                </span>
              </div>
              <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">
                AI Visibility Score
              </span>
            </motion.div>

            {/* Floating Card 2: Engines Tracked (Top-Left) */}
            <motion.div
              animate={reduceMotion ? {} : { y: [0, -6, 0], x: [0, -4, 0] }}
              transition={{
                duration: 7.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.5,
              }}
              className="absolute -left-4 top-20 z-20 flex w-48 flex-col gap-2 rounded-2xl border border-slate-200/90 bg-white p-3.5 shadow-xl backdrop-blur-sm dark:border-slate-800 dark:bg-slate-900"
            >
              <p className="font-poppins text-xs font-bold text-indigo-950 dark:text-white">
                AI Engines We Track
              </p>
              <div className="flex flex-col gap-1.5">
                {trackedEngines.map((engine) => (
                  <div
                    key={engine.name}
                    className="flex items-center gap-2 text-xs font-medium text-slate-700 dark:text-slate-300"
                  >
                    <span
                      className={`grid size-4 place-items-center rounded-full text-[9px] text-white ${engine.bg}`}
                    >
                      <FaCheck aria-hidden />
                    </span>
                    <span className="truncate">{engine.name}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Floating Card 3: Report Card (Bottom-Right) */}
            <motion.div
              animate={reduceMotion ? {} : { y: [0, -10, 0] }}
              transition={{
                duration: 6.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1,
              }}
              className="absolute -bottom-6 right-0 z-20 flex w-64 items-start gap-3 rounded-2xl border border-slate-200/90 bg-white p-4 shadow-xl backdrop-blur-sm dark:border-slate-800 dark:bg-slate-900"
            >
              <span className="grid h-14 w-11 shrink-0 place-items-center rounded-lg bg-linear-to-br from-violet-600 to-indigo-700 text-white shadow-md">
                <FaFilePdf aria-hidden className="text-xl" />
              </span>
              <div className="flex flex-col gap-1">
                <p className="font-poppins text-xs font-bold text-indigo-950 dark:text-white">
                  AI Visibility Report
                </p>
                <div className="flex flex-col gap-1 text-[11px] text-slate-600 dark:text-slate-300">
                  <span className="flex items-center gap-1.5">
                    <span className="size-1.5 rounded-full bg-pink-500" />
                    Where you&apos;re cited
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="size-1.5 rounded-full bg-pink-500" />
                    Competitor gaps
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="size-1.5 rounded-full bg-pink-500" />
                    Priority fixes
                  </span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
      <WebsiteAuditRequestPopup
        isOpen={isAuditPopupOpen}
        website={website}
        title="Get Your Free AI Visibility Report"
        description="Share your website and email to request your free AI visibility audit."
        requestName="AI Search Optimization Request"
        onWebsiteChange={setWebsite}
        onOpenChange={setIsAuditPopupOpen}
      />
    </section>
  );
}
